import React, { ReactElement } from 'react';
import type { Route } from 'next';
import classNames from 'classnames/bind';
import Image from 'next/image';
import Link from 'next/link';
import RootLayout from 'layouts/RootLayout';
import Breadcrumbs from 'components/Breadcrumbs';
import { EventCard } from 'sections/Events/components/EventCard/EventCard';
import { eventsCardsData } from 'sections/Events/data';
import { EventCardType } from 'sections/Events/components/EventCard/EventCard.types';
import useWindowSize from 'utils/hooks/useWindowSize';
import styles from 'pages/events/Events.module.scss';

const cn = classNames.bind(styles);

export default function Events() {
  const { width: screenWidth } = useWindowSize();

  const cards = eventsCardsData.map((card: EventCardType) => {
    return (
      <EventCard className={cn('event-card')} key={card.id}>

        <EventCard.Date className={cn('event-card__date')}>
          <p className={cn('event-card__date-month')}>{card.date.month}</p>
          <p className={cn('event-card__date-days')}>{card.date.days}</p>
        </EventCard.Date>

        <EventCard.Container className={cn('event-card__container')} pageUrl={card.pageUrl}>
          <EventCard.Image className={cn('event-card__image-box')}>
            {screenWidth >= 851 &&
              <Image
                className={cn('event-card__image')}
                alt={card.image.alt}
                src={card.image.desktopSrc}
                width={250}
                height={262}
                priority={card.priority}
              />
            }
            {screenWidth >= 576 && screenWidth <= 850 &&
              <Image
                className={cn('event-card__image')}
                alt={card.image.alt}
                src={card.image.tabletSrc}
                width={250}
                height={262}
                priority={card.priority}
              />
            }
            {screenWidth < 576 &&
              <Image
                className={cn('event-card__image')}
                alt={card.image.alt}
                src={card.image.mobileSrc}
                width={335}
                height={262}
                priority={card.priority}
              />
            }
          </EventCard.Image>

          <EventCard.Info className={cn('event-card__info')}>
            <div className={cn('event-card__heading-box')}>
              {card.heading.value &&
                <h3 className={cn('event-card__heading')}>{card.heading.value}</h3>
              }
              {card.heading.image &&
                <Image
                  className={cn('event-card__heading-image')}
                  alt={card.heading.imageAlt || ''}
                  src={card.heading.image}
                  width={150}
                  height={100}
                />
              }
            </div>
            <div className={cn('event-card__details')}>
              <div className={cn('event-card__details-date')}>
                <Image
                  width={24}
                  height={24}
                  src={card.details.date.icon}
                  alt="icon"
                  className={cn('event-card__details-icon')}
                />
                <span className={cn('event-card__details-value')}>{card.details.date.value}</span>
              </div>
              <div className={cn('event-card__details-country')}>
                <Image
                  width={24}
                  height={24}
                  src={card.details.country.icon}
                  alt="icon"
                  className={cn('event-card__details-icon')}
                />
                <span className={cn('event-card__details-value')}>{card.details.country.value}</span>
              </div>
              {card.details.location &&
                <div className={cn('event-card__details-location')}>
                  <Link
                    className={cn('event-card__details-location-url')}
                    href={card.details.location.url as Route}
                  >
                    {card.details.location.value}
                  </Link>
                </div>
              }
            </div>
          </EventCard.Info>

          <EventCard.Actions className={cn('event-card__actions')}>
            <div className={cn('event-card__buttons')}>
              <Link
                className={cn('event-card__button-register')}
                href={card.registrationUrl as Route}
              >
                {card.buttonTitle}
              </Link>
            </div>
            <div className={cn('event-card__logos')}>
              <Image
                width={81}
                height={18}
                src="/images/pages/Events/logo-df-alliance.svg"
                alt="DF Alliance Logo"
                className={cn('event-card__logo-icon')}
              />
              <Image
                width={67}
                height={18}
                src="/images/pages/Events/logo-dp-world.svg"
                alt="DP World Logo"
                className={cn('event-card__logo-icon')}
              />
            </div>
          </EventCard.Actions>
        </EventCard.Container>
      </EventCard>
    );
  });

  return (
    <div className={cn('events')}>
      <div className={cn('container')}>
        <Breadcrumbs mTop={64} mode="dark" links={[{ title: 'Events', href: '' as Route}]} />

        <div className={cn('events__content')}>
          <h1 className={cn('events__heading-h1')}>Upcoming Events</h1>
          <div className={cn('events__cards')}>
            {cards}
          </div>
        </div>

      </div>
    </div>
  );
}

export const getStaticProps = async () => {
  return {
    props: {
      title: "Explore Upcoming Events at DF Alliance | DF Alliance",
      description:
        'Stay updated on DF Alliance\'s latest events and industry gatherings, offering networking opportunities and insights into the future of freight forwarding.',
      keywords:
        'digital freight alliance, freight alliance, freight online, online freight marketplace, ocean freight online, logistics member portal',
      path: '/events',
    },
  };
};

Events.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
